import React from "react";
import { Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

/**
 * BidUpDialog — overlay for topping up an open commission's bounty so it
 * bumps to the top of the "Wanted near me" feeds. Lightweight portal-less
 * modal because we want it dismissible on backdrop tap; shadcn's Dialog
 * adds focus-trap behaviour we don't need here.
 *
 * Props:
 *   target   — { id, title, currentBounty } or null when hidden
 *   amount   — current slider value
 *   onAmount — slider change handler
 *   busy     — POST /bid-up in flight
 *   onSubmit, onCancel — caller handlers
 */
export const BidUpDialog = ({ target, amount, onAmount, busy, onSubmit, onCancel }) => {
  if (!target) return null;
  return (
    <div
      className="fixed inset-0 z-[2000] bg-black/70 flex items-center justify-center px-4"
      onClick={() => !busy && onCancel()}
      data-testid="bump-dialog"
    >
      <div
        className="bg-gray-900 border border-amber-500/40 rounded-lg p-5 max-w-sm w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-amber-400" />
          <h3 className="text-white font-semibold">Bid up your commission</h3>
        </div>
        <p className="text-gray-400 text-xs mb-3 line-clamp-2">&ldquo;{target.title}&rdquo;</p>
        <p className="text-gray-500 text-[11px] mb-3">
          Current bounty: <span className="text-amber-300 font-semibold">{(target.currentBounty || 0).toFixed(2)} cr</span>
          {" "}· adding more bumps your offer to the top of &ldquo;Wanted near me&rdquo; feeds.
        </p>
        <label className="text-xs text-gray-400 block mb-1">
          Add to bounty: <span className="text-amber-300 font-semibold">+{amount} credits</span>
        </label>
        <input
          type="range" min={1} max={50} step={1}
          value={amount}
          onChange={(e) => onAmount(Number(e.target.value))}
          className="w-full accent-amber-400"
          data-testid="bump-amount-slider"
        />
        <p className="text-gray-500 text-[10px] mt-1">
          You&apos;ll be charged {amount} cr + 0.10 cr bump fee = <span className="text-white font-semibold">{(amount + 0.10).toFixed(2)} cr</span> total.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold disabled:opacity-60"
            data-testid="bump-confirm-btn"
          >
            {busy ? "Bumping…" : `Bump (+${amount} cr)`}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * BoostConfirmDialog — shadcn AlertDialog wrapper for the 1-credit boost
 * action. Pure presentation; the API call lives in the parent.
 */
export const BoostConfirmDialog = ({ open, onOpenChange, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="bg-gray-900 border-gray-800 text-white" data-testid="boost-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="text-white">Boost this commission?</AlertDialogTitle>
        <AlertDialogDescription className="text-gray-400">
          Pin to the top of the feed for <span className="text-emerald-300 font-semibold">1 hour</span>.
          Costs <span className="text-amber-300 font-semibold">1 credit</span> · bounty stays the same.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel
          className="bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700"
          data-testid="boost-cancel-btn"
        >
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          className="bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold"
          data-testid="boost-confirm-btn"
        >
          Boost · 1 credit
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
